import translate, { getLangKey } from '@translate';
import React, { FC, useState, useEffect } from 'react';
import { Chart } from 'react-google-charts';
import { colors, sizes } from 'src/themes/default';
import Menu from 'src/components/Menu';
import Tooltip from 'src/components/Tooltip';
import Typography from 'src/components/Typography';
import moment, { Moment } from 'moment';
import { styled } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { getOverviewIntervalDataFetch, getOverviewIntervalTypeDataFetch } from 'src/resources/overviewInterval/reducer';
import { OverviewIntervalItem } from 'src/types/overviewIntervals';
import Grid, { GridItem } from 'src/components/Grid';
import sumArray from 'src/utils/sumArray';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    ClaimIcon,
    OrderIcon,
    ReceivingIcon,
    ReturnIcon,
    ReceivingActiveIcon,
    ClaimActiveIcon,
    ReturnActiveIcon,
} from 'src/components/SkladonIcon/Icons';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DateRange from 'src/components/DateRange';
import useWindowSize from 'src/utils/useWindowSize';
import { useDebounce } from 'src/utils/debounce';

const getMinMaxValue = (value: number, min: number, max: number): number => {
    if (value > max) {
        return max;
    }

    if (value < min) {
        return min;
    }

    return value;
};

const getTotalAndDiff = (
    actual: OverviewIntervalItem[],
    prev: OverviewIntervalItem[]
): { total: number; diff: number } => {
    const total = sumArray(actual, 'count');
    const totalPrev = sumArray(prev, 'count');
    const diff = Math.max(Math.round(((total - totalPrev) / (totalPrev || 1)) * 100));
    return { total, diff: getMinMaxValue(diff, -10000, 10000) };
};

const PaperChartBlock = styled('div')(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        // height: '400px',
    },
}));

const TimeRangeButton = styled('div')({
    display: 'inline-block',
    width: '160px',
    textAlign: 'left',

    '& svg': {
        width: '20px',
        height: '20px',
        position: 'absolute',
        right: '5px',
        top: '-2px',

        '& path': {
            fill: 'inherit',
            stroke: 'inherit',
        },
    },
});

const ChartBlock = styled('div')<{ noFullHd: boolean }>(({ theme, noFullHd }) => ({
    position: 'absolute',
    left: '10px',
    right: '10px',
    top: '160px',
    bottom: '10px',
    overflow: 'hidden',

    '& > div': {
        position: 'absolute',
        left: '10px',
        right: '10px',
        top: '0px',
        bottom: '-40px',
    },

    [theme.breakpoints.down('sm')]: {
        top: '210px',
        left: sizes.body_PD,
        right: sizes.body_PD,
    },

    ...(noFullHd && {
        top: '160px',
    }),
    ...(!noFullHd && {
        top: '110px',
    }),

    // [theme.breakpoints.up('xl')]: {
    //     top: '110px',
    // },
}));

const TimeBlock = styled('div')({
    marginTop: '10px',

    '& .MuiFormControl-root': {
        paddingTop: 0,
        paddingBottom: 0,
        maxWidth: 'unset',

        '& .MuiInputBase-root': {
            border: 'none',

            '& input': {
                padding: 0,
            },
            '& fieldset': {
                border: 'none',
            },
        },
    },
});
const TimeBlockOption = styled('div')({
    width: '100%',
    padding: '9px 24px',
});

const TotalBlock = styled('div')(({ theme }) => ({
    marginTop: '0px',
    padding: '10px 0px',
}));

const TotalItemBlock = styled('div')<{
    active?: boolean;
    activeIcon?: boolean;
}>(({ theme, active, activeIcon }) => ({
    border: '1px solid silver',
    padding: '5px 10px',
    overflow: 'hidden',
    position: 'relative',

    ...(active && {
        border: `1px solid ${colors.primary_O}`,
        // '& .chart_part_title': { color: colors.primary_O },
    }),
    ...(activeIcon && {
        '& svg path': { fill: colors.primary_O },
    }),

    [theme.breakpoints.down('sm')]: {
        border: 'none',
    },
    [theme.breakpoints.up('sm')]: {
        cursor: 'pointer',

        '& .chart_part_title': {},
    },

    '& > div': {
        '& div': {
            display: 'inline-block',
        },

        '& svg': {
            height: '28px',
            lineHeight: '28px',
            marginRight: '10px',
            verticalAlign: 'middle',
        },

        '& .chart_part_color': {
            width: '20px',
            height: '20px',
            borderRadius: '5px',
        },
        '& .chart_part_title': {
            marginRight: '8px',
            lineHeight: '28px',
        },
        '& .chart_part_count': {
            float: 'right',
            fontSize: sizes.icon_button_FS,
            fontWeight: sizes.heading_4_W,
            lineHeight: '28px',
            position: 'absolute',
            right: '70px',
        },
    },
}));

const GrowBlock = styled('div')<{
    type: 'up' | 'down';
}>(({ theme, type }) => ({
    height: '20px',
    lineHeight: '20px',
    fontSize: sizes.micro_FS,
    padding: '0px 4px',
    marginTop: '4px',
    color: type === 'down' ? '#E05252' : '#52D05F',
    backgroundColor: type === 'down' ? '#FDF1F1' : '#F1FFF6',
    float: 'right',
}));

interface MenuOptionType {
    label: string;
    value: string;
}
const TIME_OPTIONS: MenuOptionType[] = [
    { label: translate('overview.timeOption.last30Days'), value: 'last30Days' },
    { label: translate('overview.timeOption.actualMonth'), value: 'actualMonth' },
    { label: translate('overview.timeOption.lastMonth'), value: 'lastMonth' },
    { label: translate('overview.timeOption.last90Days'), value: 'last90Days' },
    { label: translate('overview.timeOption.actualYear'), value: 'actualYear' },
    { label: translate('overview.timeOption.lastYear'), value: 'lastYear' },
    { label: translate('overview.timeOption.custom'), value: 'custom' },
];

const options = {
    // seriesType: 'bars',
    // series: { 5: { type: 'line' } },
    // vAxis: { ticks: [20, 80, 130, 170], scaleType: 'log' },
    // vAxis: { scaleType: 'log' },
    colors: [colors.primary_O],
    legend: { position: 'none' },
    backgroundColor: 'transparent',
    vAxis: {
        // format: props.vAxis.format,
        viewWindow: {
            min: 0,
        },
        minValue: 0,
        // textPosition: 'none',
    },
    animation: {
        duration: 1000,
        easing: 'out',
        startup: true,
    },
    curveType: 'function',
    // width: '100%',
};

export interface TimeRangesType {
    prevStart: Moment;
    prevEnd: Moment;
    start: Moment;
    end: Moment;
    groupBy: string;
}

export type ChartItemType = (number | string)[];

export const LANG_DATE_FORMAT_TYPES = { CZ: 'D.M.', EN: 'M/D' };

const ChartContainer: FC<{ mobile?: boolean }> = (props) => {
    const dispatch = useDispatch();
    const langkey = getLangKey();
    const size = useDebounce(useWindowSize(), 500);

    const fullHd = (size.width || 0) > 1900;

    const langDateFormat = LANG_DATE_FORMAT_TYPES[langkey] || LANG_DATE_FORMAT_TYPES['CZ'];

    const loading = useSelector((state: RootStoreType) => state.overviewInterval.loading);
    const data = useSelector((state: RootStoreType) => state.overviewInterval.data);
    const ordersDataActual = useSelector((state: RootStoreType) => state.overviewInterval.ordersDataActual);
    const ordersDataPrevious = useSelector((state: RootStoreType) => state.overviewInterval.ordersDataPrevious);
    const receivingsDataActual = useSelector((state: RootStoreType) => state.overviewInterval.receivingsDataActual);
    const receivingsDataPrevious = useSelector((state: RootStoreType) => state.overviewInterval.receivingsDataPrevious);
    const returnsDataActual = useSelector((state: RootStoreType) => state.overviewInterval.returnsDataActual);
    const returnsDataPrevious = useSelector((state: RootStoreType) => state.overviewInterval.returnsDataPrevious);
    const claimsDataActual = useSelector((state: RootStoreType) => state.overviewInterval.claimsDataActual);
    const claimsDataPrevious = useSelector((state: RootStoreType) => state.overviewInterval.claimsDataPrevious);

    const [selectOption, setSelectOption] = useState<MenuOptionType>(TIME_OPTIONS[0]);
    const [selectedType, setSelectedType] = useState<string>(props.mobile ? '' : 'orders');
    const [timeRanges, setTimeRanges] = useState<TimeRangesType | undefined>();
    const [chartData, setChartData] = useState<ChartItemType[] | undefined>();
    const [totalAndDiff, setTotalAndDiff] = useState<{
        orders: number;
        ordersDiff: number;
        receivings: number;
        receivingsDiff: number;
        returns: number;
        returnsDiff: number;
        claims: number;
        claimsDiff: number;
    }>({
        orders: 0,
        ordersDiff: 0,
        receivings: 0,
        receivingsDiff: 0,
        returns: 0,
        returnsDiff: 0,
        claims: 0,
        claimsDiff: 0,
    });

    useEffect(() => {
        setTimeRanges(getTimeRanges());
    }, [selectOption]);

    useEffect(() => {
        if (timeRanges) {
            dispatch(getOverviewIntervalDataFetch(timeRanges));
        }
    }, [timeRanges]);

    useEffect(() => {
        if (timeRanges && !props.mobile) {
            dispatch(getOverviewIntervalTypeDataFetch({ time: timeRanges, type: selectedType }));
        }
    }, [selectedType, timeRanges]);

    useEffect(() => {
        if (
            !loading &&
            timeRanges &&
            ordersDataActual &&
            ordersDataPrevious &&
            receivingsDataActual &&
            receivingsDataPrevious &&
            returnsDataActual &&
            returnsDataPrevious &&
            claimsDataActual &&
            claimsDataPrevious
        ) {
            const totalData = getTotalData();
            if (totalData) {
                setTotalAndDiff({
                    orders: totalData.totalOrders.total,
                    ordersDiff: totalData.totalOrders.diff,
                    receivings: totalData.totalReceivings.total,
                    receivingsDiff: totalData.totalReceivings.diff,
                    returns: totalData.totalReturns.total,
                    returnsDiff: totalData.totalReturns.diff,
                    claims: totalData.totalClaims.total,
                    claimsDiff: totalData.totalClaims.diff,
                });
            }
        }
    }, [loading, ordersDataActual]);

    useEffect(() => {
        setChartData([['time', translate(`overview.${selectedType}`)], ...getChartData()]);
    }, [data]);

    const getChartData = () => {
        let arr: ChartItemType[] = [];
        if (timeRanges) {
            let incDate = timeRanges.start.clone();
            let option = selectOption.value;
            if ((option = 'custom')) {
                const daysDiff = timeRanges.end.diff(timeRanges.start, 'd');
                if (daysDiff < 32) {
                    option = 'day';
                } else if (daysDiff > 90) {
                    option = 'year';
                } else {
                    option = 'month';
                }
            }
            switch (option) {
                case 'actualMonth':
                case 'lastMonth':
                case 'last30Days':
                case 'day':
                    while (incDate < timeRanges.end) {
                        const dayData = getDayData(incDate);
                        arr.push([incDate.format(langDateFormat), Number(dayData?.count || 0)]);
                        incDate.add(1, 'day');
                    }

                    break;

                case 'last90Days':
                case 'month':
                    const startDayInWeek = incDate.day();
                    while (incDate < timeRanges.end) {
                        let sumDay = 0;

                        // time string becouse inc can be ower timerange
                        const startTimeStr = incDate.format(langDateFormat);
                        let endTimeStr = incDate.format(langDateFormat);
                        const dayData = getDayData(incDate);
                        sumDay += Number(dayData?.count || 0);
                        incDate.add(1, 'day');

                        while (incDate.day() !== startDayInWeek && incDate < timeRanges.end) {
                            const dayData = getDayData(incDate);
                            sumDay += Number(dayData?.count || 0);
                            endTimeStr = incDate.format(langDateFormat);
                            incDate.add(1, 'day');
                        }
                        arr.push([`${startTimeStr} - ${endTimeStr}`, sumDay]);
                    }
                    break;

                case 'actualYear':
                case 'lastYear':
                case 'year':
                    while (incDate < timeRanges.end) {
                        const month = incDate.month() + 1;
                        const monthData = data?.find((item) => item.month === month);
                        arr.push([incDate.format('MMM'), Number(monthData?.count || 0)]);
                        incDate.add(1, 'month');
                    }
                    break;
            }
        }
        return arr;
    };

    const getTotalData = () => {
        if (
            ordersDataActual &&
            ordersDataPrevious &&
            receivingsDataActual &&
            receivingsDataPrevious &&
            returnsDataActual &&
            returnsDataPrevious &&
            claimsDataActual &&
            claimsDataPrevious
        ) {
            return {
                totalOrders: getTotalAndDiff(ordersDataActual, ordersDataPrevious),
                totalReceivings: getTotalAndDiff(receivingsDataActual, receivingsDataPrevious),
                totalReturns: getTotalAndDiff(returnsDataActual, returnsDataPrevious),
                totalClaims: getTotalAndDiff(claimsDataActual, claimsDataPrevious),
            };
        }
        return null;
    };

    const getTimeRanges: () => {
        prevStart: Moment;
        prevEnd: Moment;
        start: Moment;
        end: Moment;
        groupBy: string;
    } = () => {
        let prevStart = moment();
        let prevEnd = moment();
        let start = moment();
        let end = moment();
        let groupBy: string = 'day';
        switch (selectOption.value) {
            case 'actualMonth':
                prevStart.subtract(1, 'months').startOf('month');
                start.startOf('month');
                end.endOf('month');
                break;
            case 'lastMonth':
                prevStart.subtract(2, 'months').startOf('month');
                start.subtract(1, 'months').startOf('month');
                end.subtract(1, 'months').endOf('month');
                break;
            case 'last30Days':
                prevStart.subtract(59, 'day').startOf('day');
                start.subtract(29, 'day').startOf('day');
                end.endOf('day');
                break;
            case 'last90Days':
                prevStart.subtract(179, 'day').startOf('day');
                start.subtract(89, 'day').startOf('day');
                end.endOf('day');
                break;
            case 'actualYear':
                prevStart.subtract(1, 'year').startOf('year');
                start.startOf('year');
                end.endOf('year');
                groupBy = 'month';
                break;
            case 'lastYear':
                prevStart.subtract(2, 'year').startOf('year');
                start.subtract(1, 'year').startOf('year');
                end.subtract(1, 'year').endOf('year');
                groupBy = 'month';
                break;
            case 'custom':
                if (timeRanges) {
                    return timeRanges;
                }
        }

        prevEnd = start.clone().subtract(1, 'second');
        const actual = moment().endOf('day');

        switch (selectOption.value) {
            case 'actualMonth':
                const minM = Math.min(prevEnd.get('date'), actual.get('date'));
                prevEnd.set('date', minM);
                break;
            case 'actualYear':
                prevEnd.set('month', actual.get('month')).set('date', actual.get('date'));
                break;
        }

        return { prevStart, prevEnd, start, end, groupBy };
    };

    const getDayData = (incDate: Moment): OverviewIntervalItem | null => {
        if (data) {
            const day = incDate.date();
            const month = incDate.month() + 1;
            const dayData = data.find((item) => item.day === day && item.month === month);

            return dayData || null;
        }
        return null;
    };

    const onChangeOption = (option: MenuOptionType) => {
        setSelectOption(option);
    };

    const onChangeType = (type: string) => {
        if (!props.mobile) {
            setSelectedType(type);
        }
    };

    const onChangeCustom = (dates: string[] | undefined) => {
        if (!dates) {
            return;
        }
        let groupBy: string = 'day';
        let start = moment(dates[0]);
        let end = moment(dates[1]);
        let prevEnd = start.clone().subtract(1, 'second');
        const daysDiff = end.diff(start, 'd');
        let prevStart = start.clone().subtract(daysDiff, 'second');
        if (daysDiff > 90) {
            groupBy = 'month';
        }

        setTimeRanges({ prevStart, prevEnd, start, end, groupBy });
    };

    const ordersTotalBlock = (
        <TotalItemBlock
            onClick={() => onChangeType('orders')}
            active={selectedType === 'orders'}
            activeIcon={selectedType === 'orders'}
        >
            <div>
                <OrderIcon />
                <div className="chart_part_title">{translate('overview.orders')}</div>
                <GrowBlock type={totalAndDiff.ordersDiff > 0 ? 'up' : 'down'}>
                    {totalAndDiff.ordersDiff > 0 ? '+' : ''}
                    {totalAndDiff.ordersDiff}%
                </GrowBlock>
                <div className="chart_part_count">{totalAndDiff.orders}</div>
            </div>
        </TotalItemBlock>
    );
    const receivingsTotalBlock = (
        <TotalItemBlock onClick={() => onChangeType('receivings')} active={selectedType === 'receivings'}>
            <div>
                {selectedType === 'receivings' ? <ReceivingActiveIcon /> : <ReceivingIcon />}
                <div className="chart_part_title">{translate('overview.receivings')}</div>
                <GrowBlock type={totalAndDiff.receivingsDiff > 0 ? 'up' : 'down'}>
                    {totalAndDiff.receivingsDiff > 0 ? '+' : ''}
                    {totalAndDiff.receivingsDiff}%
                </GrowBlock>
                <div className="chart_part_count">{totalAndDiff.receivings}</div>
            </div>
        </TotalItemBlock>
    );
    const returnsTotalBlock = (
        <TotalItemBlock onClick={() => onChangeType('returns')} active={selectedType === 'returns'}>
            <div>
                {selectedType === 'returns' ? <ReturnActiveIcon /> : <ReturnIcon />}
                <div className="chart_part_title">{translate('overview.returns')}</div>
                <GrowBlock type={totalAndDiff.returnsDiff > 0 ? 'up' : 'down'}>
                    {totalAndDiff.returnsDiff > 0 ? '+' : ''}
                    {totalAndDiff.returnsDiff}%
                </GrowBlock>
                <div className="chart_part_count">{totalAndDiff.returns}</div>
            </div>
        </TotalItemBlock>
    );
    const claimsTotalBlock = (
        <TotalItemBlock onClick={() => onChangeType('claims')} active={selectedType === 'claims'}>
            <div>
                {selectedType === 'claims' ? <ClaimActiveIcon /> : <ClaimIcon />}
                <div className="chart_part_title">{translate('overview.claims')}</div>
                <GrowBlock type={totalAndDiff.claimsDiff > 0 ? 'up' : 'down'}>
                    {totalAndDiff.claimsDiff > 0 ? '+' : ''}
                    {totalAndDiff.claimsDiff}%
                </GrowBlock>
                <div className="chart_part_count">{totalAndDiff.claims}</div>
            </div>
        </TotalItemBlock>
    );

    return (
        <PaperChartBlock>
            <TimeBlock>
                <Menu
                    variant="menu"
                    inline
                    text={
                        <Tooltip title={translate('overview.timeRange')}>
                            <TimeRangeButton>
                                <Typography variant="body1" component="span">
                                    {selectOption.label}
                                    <ArrowDropDownIcon />
                                </Typography>
                            </TimeRangeButton>
                        </Tooltip>
                    }
                    textSx={{
                        padding: '0',
                        marginRight: '20px',
                        backgroundColor: colors.background_W,
                        '&:hover': { backgroundColor: colors.background_W },
                        '&:active': { backgroundColor: colors.background_W },
                        '&:active:hover': { backgroundColor: colors.background_W },
                    }}
                    sx={{ '&  li': { minHeight: 'auto', padding: 0 } }}
                    items={TIME_OPTIONS.map((option) => (
                        <TimeBlockOption key={option.value} onClick={() => onChangeOption(option)}>
                            {translate(`${option.label}`)}
                        </TimeBlockOption>
                    ))}
                />
                <Typography
                    variant="body2"
                    component={props.mobile ? undefined : 'span'}
                    sx={{ color: colors.text_T2 }}
                >
                    {translate('overview.timePreview')}:{' '}
                    <DateRange
                        required
                        // value={timeRanges ? [timeRanges.start.format('L'), timeRanges.end.format('L')] : undefined}
                        value={timeRanges ? [timeRanges.start.format(), timeRanges.end.format()] : undefined}
                        disabled={selectOption.value !== 'custom'}
                        onChange={onChangeCustom}
                        format="L"
                    />
                </Typography>
            </TimeBlock>

            <TotalBlock>
                {fullHd || props.mobile ? (
                    <Grid spacing={1}>
                        <GridItem cols={3}>{ordersTotalBlock}</GridItem>
                        <GridItem cols={3}>{receivingsTotalBlock}</GridItem>
                        <GridItem cols={3}>{returnsTotalBlock}</GridItem>
                        <GridItem cols={3}>{claimsTotalBlock}</GridItem>
                    </Grid>
                ) : (
                    <>
                        <Grid spacing={1}>
                            <GridItem cols={6}>{ordersTotalBlock}</GridItem>
                            <GridItem cols={6}>{receivingsTotalBlock}</GridItem>
                        </Grid>
                        <Grid spacing={1}>
                            <GridItem cols={6}>{returnsTotalBlock}</GridItem>
                            <GridItem cols={6}>{claimsTotalBlock}</GridItem>
                        </Grid>
                    </>
                )}
            </TotalBlock>
            {!props.mobile && (
                <ChartBlock noFullHd={!fullHd}>
                    <div>
                        {!!((chartData?.length || 0) > 1) && (
                            <Chart
                                chartType="Line"
                                width="100%"
                                height="calc(100% - 7px)"
                                data={chartData}
                                options={options}
                            />
                        )}
                    </div>
                </ChartBlock>
            )}
        </PaperChartBlock>
    );
};

export default ChartContainer;
